import { useHistory } from 'react-router-dom';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Banner from '@cpg-its/ads.containers.banner';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { Form, Formik, FormikProps } from 'formik';
import { Grid } from '@mui/material';
import { CompensationProps } from '../../../models/arc-epi-props';
import { FormWrapper } from '../../../../../global/components/form-wrapper';
import { LabelSettingProps } from '../../../../../global/models/epi-model';
import { ScheduledHoursFormValues, ScheduledHoursValidationSchema } from './scheduled-hours-form-model';
import { NumberField, NumberInputTypes } from '../../../../../global/components/form-element/number-field';
import { ScheduledHours } from '../../employment/employment-model';
import { formatDate, MinDate } from '../../../../../global/helpers/form-data-helper';
import { FormField } from '../../../../../global/components/form-element/form-field';
import { FormGroup } from '../../../../../global/components/layout/form-group';
import { DividerSection } from '../../../../../global/components/layout/divider-section';
import { getFormattedDate } from '../../../../../global/helpers/date-helpers';
import { useApiResponseError } from '../../../../../global/hooks/useApiError';
import { ContentSpacer } from '../../../../../global/components/layout/generic-content-spacer';
import { YesNoOptions } from '../../../../../global/constants/lists';
import { YesNoIndicators } from '../../../../../global/models/api-model';
import { WysiwygWithReactLink } from '../../../../../global/components/wysiwyg-with-react-link';
import { sortTableStyles } from '../../../../../global/constants/theme/styles/tables/generic-sort-table';
import { TcsmStates } from '../../../../../root/tcsm-model';
import { getBenefitEndDate } from '../../../../../global/ducks/benadmin-api';
import { FormItem } from '../../../../../Global/components/layout/form-item';

interface ScheduledPaymentFormProps {
    onSubmitHandler: (savedValues: ScheduledHoursFormValues, values: ScheduledHoursFormValues) => any;
    compensationProps: CompensationProps;
    labelSettings: LabelSettingProps;
    currentScheduledHours: ScheduledHours;
    previousRoute: string;
    isEdit: boolean;
}
export function ScheduledHoursForm(props: React.PropsWithChildren<ScheduledPaymentFormProps>) {
    const {
        onSubmitHandler,
        compensationProps,
        labelSettings,
        previousRoute,
        isEdit,
        currentScheduledHours } = props;

    const history = useHistory();
    const apiErrors = useApiResponseError();
    const tableClasses = sortTableStyles();

    const initialValues: ScheduledHoursFormValues = {
        start_date: isEdit ? getFormattedDate(currentScheduledHours?.start_date) : null,
        hours: currentScheduledHours?.hours || 0,
        changeDueToIllness: '',
        extenuatingCircumstances: '',
        acknowledge: false,
    };

    const currentRecordCompareDate = currentScheduledHours?.end_date || currentScheduledHours?.start_date || formatDate(MinDate);
    const [productTerminationDatesLoaded, setProductTerminationDatesLoaded] = useState(true);
    const [productTerminationDates, setProductTerminationDates] = useState([]);
    const guid = useSelector<TcsmStates, string>(state => state?.employmentState?.currentEmployee?.guid);
    const employmentId = useSelector<TcsmStates, number>(state => state?.employmentState?.currentEmployment?.id);
    const dispatch = useDispatch();


    function onSuccess(res) {
        setProductTerminationDatesLoaded(true);
        setProductTerminationDates(res?.data);
    }
    function onFailure() {
        setProductTerminationDatesLoaded(true);
    }
    function handleBlur(e, formValues: ScheduledHoursFormValues, formField: string) {
        const value = e instanceof Date ? getFormattedDate(e?.toISOString()) : e?.target?.value;
        if (formField === 'changeDueToIllness' && formValues.hours && formValues.start_date) {
            setProductTerminationDatesLoaded(false);
            dispatch(getBenefitEndDate(guid, employmentId, formValues.hours, getFormattedDate(formValues.start_date), value, onSuccess, onFailure));
        }
        if (formField === 'hours' && formValues.changeDueToIllness && formValues.start_date) {
            setProductTerminationDatesLoaded(false);
            dispatch(getBenefitEndDate(guid, employmentId, value, getFormattedDate(formValues.start_date), formValues.changeDueToIllness, onSuccess, onFailure));
        }
        if (formField === 'start_date' && formValues.hours && formValues.changeDueToIllness) {
            setProductTerminationDatesLoaded(false);
            dispatch(getBenefitEndDate(guid, employmentId, formValues.hours, value, formValues.changeDueToIllness, onSuccess, onFailure));
        }
    }

    function renderDecreaseInScheduledHour(formProps: FormikProps<ScheduledHoursFormValues>) {

        if (!formProps?.values?.hours || formProps?.values?.hours >= 1000) {
            return null;
        }

        return (
            <>
                <FormField
                    name='changeDueToIllness'
                    label={compensationProps.illnessLabel}
                    component='radioGroup'
                    options={YesNoOptions}
                    errors={formProps.errors}
                    touched={formProps.touched}
                    onChange={(e) => handleBlur(e, formProps?.values, 'changeDueToIllness')}
                />
                {
                    formProps?.values?.changeDueToIllness === YesNoIndicators.Yes &&
                    <FormItem>
                        <div data-formdata='acknowledge'>
                            <FormField
                                name='acknowledge'
                                label={compensationProps.acknowlegeIllness}
                                component='checkBox'
                                touched={formProps.touched}
                                errors={formProps.errors}
                                value={formProps?.values?.acknowledge ?? false}
                                checked={formProps?.values?.acknowledge ?? false}
                            />
                        </div>
                    </FormItem>

                }
                <FormItem>
                    <ContentSpacer>
                        <p><strong>{compensationProps.extenuatingCircumstances}</strong></p>
                        <p>{compensationProps?.extenuatingCircumstancesDescription}</p>
                        <FormField
                            name='extenuatingCircumstances'
                            component='input'
                            multiline
                            rows={5}
                            touched={formProps.touched}
                            errors={formProps.errors}
                            placeholder={compensationProps?.extenuatingCircumstancesPlaceholderText}

                        />
                    </ContentSpacer>
                </FormItem>
                {!productTerminationDatesLoaded && <LoadingCircle />}
                {productTerminationDates?.length > 0 &&
                    <DividerSection removeDivider sectionText={compensationProps?.terminationDateSectionTitle} >
                        <div className={tableClasses.sortTable}>

                            <div className='sort-table-content'>
                                {
                                    productTerminationDates?.map(a =>
                                        <div className='sort-table-row' key={a.plan_type}>
                                            <div className='sort-table-row-cell'>
                                                <p>{a?.plan_name}</p>
                                            </div>
                                            <div className='sort-table-row-cell'>
                                                <p>{getFormattedDate(a?.end_date)}</p>
                                            </div>
                                        </div>)
                                }
                            </div>
                        </div>
                    </DividerSection>}
                <ContentSpacer>
                    <Banner banner={{
                        type: 'warning',
                        label: <WysiwygWithReactLink content={compensationProps?.terminationWarningMessage} />,
                    }} />
                </ContentSpacer>
            </>
        );
    }


    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(values) => onSubmitHandler(initialValues, values)}
            validationSchema={ScheduledHoursValidationSchema(compensationProps, labelSettings, isEdit, currentRecordCompareDate)}
            enableReinitialize
            initialErrors={apiErrors}
        >
            {(formProps: FormikProps<ScheduledHoursFormValues>) => (
                <Form>
                    <Grid>
                        <FormWrapper
                            formikProps={formProps}
                            submitText={labelSettings.submitText}
                            cancelText={labelSettings.cancelText}
                            onCancel={() => history.push(previousRoute)}
                        >
                            {props.children}
                            <DividerSection removeDivider>
                                <FormGroup>
                                    <FormItem>
                                        <NumberField
                                            type={NumberInputTypes.Integer}
                                            name='hours'
                                            label={compensationProps?.scheduledHoursLabel}
                                            formikProps={formProps}
                                            autoWidth
                                            required
                                            onBlur={(e) => handleBlur(e, formProps?.values, 'hours')}

                                        />
                                        {
                                            formProps?.values?.hours < 1000 &&
                                            <WysiwygWithReactLink content={compensationProps?.below1000Message} />
                                        }
                                    </FormItem>
                                    <FormField
                                        name='start_date'
                                        label={compensationProps?.editScheduledHoursStartDateLabel}
                                        component='datePicker'
                                        touched={formProps.touched}
                                        errors={formProps.errors}
                                        required
                                        disabled={isEdit}
                                        autoWidth
                                        onBlur={(e) => handleBlur(e, formProps?.values, 'start_date')}

                                    />


                                    {
                                        renderDecreaseInScheduledHour(formProps)
                                    }
                                </FormGroup>
                            </DividerSection>
                        </FormWrapper>
                    </Grid>
                </Form>
            )}
        </Formik >
    );
}